import { Link, useNavigate } from '@tanstack/react-router';
import { CalendarCheck, Send } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import {
  INTERVIEW_LENGTH_CHOICES,
  OFFER_SLOTS_MAX,
  PANELIST_STATE_LABELS,
  SCHEDULER_HORIZON_DAYS,
  UNBOOKED_INTERVIEW_STATUSES,
  type InterviewView,
  type RoomOption,
  type SchedulerView,
  type SlotView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { PANELIST_STATE_TONES } from '../../../components/status-tones';
import { EmptyState, ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { dhakaToIso } from '../../../lib/dhaka-time';
import { formatClockTime, formatTime } from '../../../lib/format';
import { useNow } from '../../../lib/use-now';
import { useScheduler, useTimeCheck } from '../api/interviews-api';
import { DhakaTimeFields, WarningList } from '../components/InterviewPanels';
import { useInterviewActions, useViewerZone } from '../hooks/use-interview-actions';
import { byDay, dayHeading, panelNames, roundLine } from '../interview-ui';
import styles from '../components/Interviews.module.css';

/**
 * SCH-01 (F10.02–F10.05). §L5: slots the round's length apart plus buffer, inside the
 * department's calendar, from the minimum notice on; only full-panel slots unless
 * "show partial" is on. Pick 1–10 to send (S19 Q2: not held), or one to book, or type a
 * time agreed on the phone — a clash with a panelist's other interview is a warning the
 * recruiter accepts, never a silent double-booking.
 */
export function SchedulerPage({ interviewId }: { readonly interviewId: string }): ReactNode {
  const query = useScheduler(interviewId);
  const navigate = useNavigate();

  if (query.isPending) return <SkeletonLines count={12} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="scheduling this interview" contact="The recruiter for this job" />;
    if (query.error.status === 404) return <NotFoundState onGoBack={() => void navigate({ to: '/interviews/to-schedule' })} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  return <Scheduler key={query.data.interview.version} data={query.data} />;
}

function Scheduler({ data }: { readonly data: SchedulerView }): ReactNode {
  const { interview, slots, rooms, onCalendarHours } = data;
  const navigate = useNavigate();
  const zone = useViewerZone();
  const actions = useInterviewActions(interview.id);
  const [isPartialShown, setIsPartialShown] = useState(false);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [isSending, setIsSending] = useState(false);
  const names = useMemo(() => new Map(interview.panel.map((member) => [member.userId, member.fullName])), [interview.panel]);

  const full = slots.filter((slot) => slot.isFullPanel);
  const shown = isPartialShown ? slots : full;
  const days = byDay(shown, (slot) => slot.startsAt, zone);
  const isOpen = UNBOOKED_INTERVIEW_STATUSES.includes(interview.status);
  const backToInterview = (): void => void navigate({ to: '/interviews/$interviewId', params: { interviewId: interview.id } });

  const toggle = (startsAt: string): void =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(startsAt)) next.delete(startsAt);
      else if (next.size < OFFER_SLOTS_MAX) next.add(startsAt);
      return next;
    });

  const send = async (startsAt: readonly string[]): Promise<void> => {
    setIsSending(true);
    const isSent = await actions.offer(interview, startsAt);
    setIsSending(false);
    if (isSent) backToInterview();
  };

  return (
    <div className={styles.screen}>
      <Link to="/interviews/$interviewId" params={{ interviewId: interview.id }} className={styles.back}>
        ← {interview.candidateName}
      </Link>
      <Card title={`Schedule ${interview.candidateName}`} meta={`${interview.jobTitle} · ${roundLine(interview)} · ${panelNames(interview)}`}>
        {!isOpen && <p className={styles.notice}>This interview is already booked. Use "Move" on the interview to change its time.</p>}
        {onCalendarHours.length > 0 && (
          <p className={styles.notice}>
            {onCalendarHours.join(', ')} {onCalendarHours.length === 1 ? 'has' : 'have'} not set working hours, so the working calendar is used for {onCalendarHours.length === 1 ? 'them' : 'them all'}.
          </p>
        )}
        <div className={styles.toolbar}>
          <Checkbox label="Show partial availability" checked={isPartialShown} onChange={setIsPartialShown} />
          <span className={styles.quiet}>
            {full.length} full-panel time{full.length === 1 ? '' : 's'} in the next {SCHEDULER_HORIZON_DAYS} days · {interview.durationMinutes} min + {interview.bufferMinutes} min buffer · at least {interview.minimumNoticeHours}h notice
          </span>
        </div>
      </Card>

      {isOpen && (
        <Card title="Free times" meta={isPartialShown ? 'Every time, with who is free' : 'Times when the whole counting panel is free'}>
          {days.length === 0 ? (
            <EmptyState
              isInset
              title={isPartialShown ? 'No times in the next two weeks' : 'No time suits the whole panel'}
              body={isPartialShown ? 'The working calendar has no open days ahead. Book a time agreed by phone below.' : 'Show partial availability to see times where most of the panel is free, or book a time agreed by phone below.'}
              action={
                isPartialShown ? null : (
                  <Button variant="solid" size="sm" onClick={() => setIsPartialShown(true)}>
                    Show partial availability
                  </Button>
                )
              }
            />
          ) : (
            <div className={styles.days}>
              {days.map((group) => (
                <section key={group.day} aria-label={dayHeading(group.day)}>
                  <h3 className={styles.dayHeading}>{dayHeading(group.day)}</h3>
                  <div className={styles.slots}>
                    {group.rows.map((slot) => (
                      <SlotButton key={slot.startsAt} slot={slot} zone={zone} names={names} isChosen={chosen.has(slot.startsAt)} onToggle={() => toggle(slot.startsAt)} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </Card>
      )}

      {isOpen && <BookByHand interview={interview} rooms={rooms} chosen={chosen.size === 1 ? [...chosen][0] ?? null : null} onBooked={backToInterview} />}

      {isOpen && (
        <div className={styles.stickyBar}>
          <span className={styles.stickyNote}>
            {chosen.size === 0
              ? interview.isSelfBookingAllowed
                ? `This round sends every free time — or pick up to ${OFFER_SLOTS_MAX} yourself.`
                : `Pick up to ${OFFER_SLOTS_MAX} times to send, or one to book.`
              : `${chosen.size} picked${chosen.size === OFFER_SLOTS_MAX ? ' (the most you can send)' : ''}`}
          </span>
          {chosen.size === 0 && interview.isSelfBookingAllowed && (
            <Button variant="solid" icon={Send} isPending={isSending} disabled={full.length === 0} onClick={() => void send([])}>
              Send every free time ({full.length})
            </Button>
          )}
          {chosen.size > 0 && (
            <>
              <Button variant="ghost" onClick={() => setChosen(new Set())}>
                Clear
              </Button>
              <Button variant="solid" icon={Send} isPending={isSending} onClick={() => void send([...chosen])}>
                Send {chosen.size} time{chosen.size === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function SlotButton({
  slot,
  zone,
  names,
  isChosen,
  onToggle,
}: {
  readonly slot: SlotView;
  readonly zone: string;
  readonly names: ReadonlyMap<string, string>;
  readonly isChosen: boolean;
  readonly onToggle: () => void;
}): ReactNode {
  const busy = slot.panel.filter((state) => state.state !== 'available');
  return (
    <button type="button" className={styles.slot} aria-pressed={isChosen} onClick={onToggle}>
      <span className={styles.slotTime}>
        {formatClockTime(slot.startsAt, { timeZone: zone })} – {formatTime(slot.endsAt, { timeZone: zone })}
      </span>
      {busy.length > 0 && (
        <span className={styles.slotStates}>
          {busy.map((state) => (
            <Chip key={state.userId} tone={PANELIST_STATE_TONES[state.state]} plain>
              {names.get(state.userId) ?? 'Panelist'}: {PANELIST_STATE_LABELS[state.state].toLowerCase()}
            </Chip>
          ))}
        </span>
      )}
    </button>
  );
}

/**
 * F10.05 — book directly: one picked slot, or a time agreed on the phone. The clash check
 * runs as the time is typed, so the warning is on screen before the button is pressed.
 */
function BookByHand({
  interview,
  rooms,
  chosen,
  onBooked,
}: {
  readonly interview: InterviewView;
  readonly rooms: readonly RoomOption[];
  readonly chosen: string | null;
  readonly onBooked: () => void;
}): ReactNode {
  const actions = useInterviewActions(interview.id);
  const zone = useViewerZone();
  const now = useNow();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [link, setLink] = useState(interview.meetingLink ?? '');
  const [roomId, setRoomId] = useState<string | null>(interview.roomId);
  // C5-c (ADR 0077): an hour unless this one booking says otherwise.
  const [minutes, setMinutes] = useState(interview.durationMinutes);
  const [refusal, setRefusal] = useState<{ readonly attempt: string; readonly warnings: readonly string[] } | null>(null);
  const [isPending, setIsPending] = useState(false);
  const typed = date && time ? dhakaToIso(date, time) : null;
  const startsAt = chosen ?? typed;
  // The server's clash refusal holds only for the time, room and length it answered: change one and it is gone, so
  // "Book anyway" can never accept another time's clashes (D92).
  const attempt = `${startsAt}|${roomId}|${minutes}`;
  const warnings = refusal?.attempt === attempt ? refusal.warnings : [];
  const check = useTimeCheck(interview.id, startsAt, interview.mode === 'in_person' ? roomId : null);
  const current = check.data?.startsAt === startsAt ? check.data : undefined;
  const clashes = current?.warnings ?? [];
  const isRoomTaken = current?.isRoomTaken === true;
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka' }).format(now);
  const trimmedLink = link.trim();

  const book = async (isConflictAccepted: boolean): Promise<void> => {
    if (!startsAt) return;
    setIsPending(true);
    const result = await actions.book(interview, {
      startsAt,
      isConflictAccepted,
      ...(interview.mode === 'online' ? { meetingLink: trimmedLink === '' ? null : trimmedLink } : {}),
      ...(interview.mode === 'in_person' ? { roomId } : {}),
      ...(minutes !== interview.durationMinutes ? { durationMinutes: minutes } : {}),
    });
    setIsPending(false);
    if (Array.isArray(result)) setRefusal({ attempt, warnings: result });
    else if (result) onBooked();
  };

  const shownWarnings = warnings.length > 0 ? warnings : clashes;
  return (
    <Card
      title={chosen ? `Book ${formatTime(chosen, { timeZone: zone })}` : 'Book a time agreed by phone'}
      meta={chosen ? 'The picked time, booked straight away — no email choice for the candidate' : 'Any time; the panel is checked as you type'}
    >
      {!chosen && <DhakaTimeFields date={date} time={time} onDate={setDate} onTime={setTime} min={today} />}
      <div className={styles.fieldRow}>
        <Field label="Length" hint="For the calendar invites. This interview only.">
          {({ inputId, describedBy }) => (
            <Select
              id={inputId}
              describedBy={describedBy}
              label="Length"
              value={String(minutes)}
              options={[...new Set([...INTERVIEW_LENGTH_CHOICES, interview.durationMinutes])].sort((a, b) => a - b).map((choice) => ({ value: String(choice), label: lengthLabel(choice) }))}
              onChange={(value) => setMinutes(Number(value))}
            />
          )}
        </Field>
        {interview.mode === 'online' && (
          <Field label="Meeting link" isOptional hint="Or add it later">
            {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} value={link} placeholder="https://meet.google.com/…" onChange={(event) => setLink(event.target.value)} />}
          </Field>
        )}
        {interview.mode === 'in_person' && (
          <Field label="Room" isOptional {...(isRoomTaken ? { error: 'That room is booked then. Choose another room or time.' } : {})}>
            {({ inputId, isInvalid }) => (
              <Select
                id={inputId}
                label="Room"
                value={roomId}
                isInvalid={isInvalid}
                placeholder={rooms.length === 0 ? 'No rooms set up yet' : 'Choose a room'}
                disabled={rooms.length === 0}
                options={rooms.map((room) => ({ value: room.id, label: room.name }))}
                onChange={setRoomId}
              />
            )}
          </Field>
        )}
      </div>
      <WarningList warnings={shownWarnings} />
      <div className={styles.actionsRow}>
        <Button variant={shownWarnings.length > 0 ? 'outline' : 'solid'} icon={CalendarCheck} isPending={isPending} disabled={!startsAt || isRoomTaken} onClick={() => void book(shownWarnings.length > 0)}>
          {shownWarnings.length > 0 ? 'Book anyway' : 'Book this time'}
        </Button>
        <span className={styles.quiet}>The candidate gets the time with a link to confirm; the panel is told.</span>
      </div>
    </Card>
  );
}

const MINUTES_PER_HOUR = 60;

/** "1 hour", "45 minutes", "1 hour 30 minutes". */
function lengthLabel(minutes: number): string {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const rest = minutes % MINUTES_PER_HOUR;
  const parts = [hours > 0 ? `${hours} hour${hours === 1 ? '' : 's'}` : '', rest > 0 ? `${rest} minutes` : ''].filter(Boolean);
  return parts.join(' ');
}
